import type { MessageDescriptor } from '@lingui/core';
import { msg } from '@lingui/core/macro';
import { useLingui } from '@lingui/react';
import { Check, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Label } from '@/components/ui/label';
import { QueryErrorState } from '@/components/ui/query-error-state';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import {
  useNotificationPreferences,
  useUpdateNotificationPreference,
  type NotificationPreferenceKey,
} from '@/hooks/useNotificationPreferences';
import { cn } from '@/lib/utils';

export const NOTIFICATION_DIALOG_TITLE = msg({ id: 'notifications.dialog.title', message: 'Notifications' });

interface KindRow {
  key: NotificationPreferenceKey;
  label: MessageDescriptor;
  hint: MessageDescriptor;
}

interface KindGroup {
  key: string;
  title: MessageDescriptor;
  kinds: readonly KindRow[];
}

/**
 * The kinds the management app sends, in its three groups and order
 * (mobile-management notifications-settings). The ids and default messages are
 * the shared mobile ones (mobile-shared NotificationPreferencesPanel and the
 * management screen), so a kind has one name on the phone and on the web.
 */
const MANAGEMENT_GROUPS: readonly KindGroup[] = [
  {
    key: 'children',
    title: msg({ id: 'management.settings.notifications.children', message: 'Messages and children' }),
    kinds: [
      {
        key: 'newMessage',
        label: msg({ id: 'notifications.kind.newMessage', message: 'New messages' }),
        hint: msg({
          id: 'management.settings.notifications.hint.newMessage',
          message: 'When a parent or a colleague writes to you or the office.',
        }),
      },
      {
        key: 'accidentNotification',
        label: msg({ id: 'notifications.kind.accidentNotification', message: 'Accident notifications' }),
        hint: msg({
          id: 'management.settings.notifications.hint.accidentNotification',
          message: 'When the team records an accident for your review.',
        }),
      },
      {
        key: 'childAbsenceReported',
        label: msg({ id: 'notifications.kind.childAbsenceReported', message: 'Absences reported by parents' }),
        hint: msg({
          id: 'notifications.kind.childAbsenceReported.hint',
          message: 'When a parent reports, changes or withdraws an absence.',
        }),
      },
      {
        key: 'pickupException',
        label: msg({ id: 'notifications.kind.pickupException', message: 'Pick-up by a person not on the list' }),
        hint: msg({
          id: 'management.settings.notifications.hint.pickupException',
          message: 'When a child leaves with someone who is not on the pick-up list.',
        }),
      },
    ],
  },
  {
    key: 'team',
    title: msg({ id: 'management.settings.notifications.team', message: 'Team' }),
    kinds: [
      {
        key: 'staffAbsenceSubmitted',
        label: msg({
          id: 'notifications.kind.staffAbsenceSubmitted',
          message: 'New absence requests and reports from staff',
        }),
        hint: msg({
          id: 'management.settings.notifications.hint.staffAbsenceSubmitted',
          message: 'When an employee requests leave, reports an absence or sends a certificate.',
        }),
      },
      {
        key: 'absenceDecision',
        label: msg({ id: 'notifications.kind.absenceDecision', message: 'Decisions on my absences' }),
        hint: msg({
          id: 'management.settings.notifications.hint.absenceDecision',
          message: 'When someone decides on an absence of your own.',
        }),
      },
      {
        key: 'employeeDataSubmitted',
        label: msg({ id: 'notifications.kind.employeeDataSubmitted', message: 'Personal data requests from staff' }),
        hint: msg({
          id: 'management.settings.notifications.hint.employeeDataSubmitted',
          message: 'When an employee asks to change their address, phone or emergency contact.',
        }),
      },
      {
        key: 'shiftRequestReceived',
        label: msg({ id: 'notifications.kind.shiftRequestReceived', message: 'Shift requests to decide' }),
        hint: msg({
          id: 'notifications.kind.shiftRequestReceived.hint',
          message: 'When an employee asks for an open shift or a swap waits for your decision.',
        }),
      },
    ],
  },
  {
    key: 'finance',
    title: msg({ id: 'management.settings.notifications.finance', message: 'Finance' }),
    kinds: [
      {
        key: 'dunningAwaitingApproval',
        label: msg({ id: 'notifications.kind.dunningAwaitingApproval', message: 'Payment reminders to approve' }),
        hint: msg({
          id: 'notifications.kind.dunningAwaitingApproval.hint',
          message: 'When a dunning run leaves reminders for your approval. One message per run.',
        }),
      },
      {
        key: 'dunningStageSent',
        label: msg({ id: 'notifications.kind.dunningStageSent', message: 'Payment reminders sent' }),
        hint: msg({
          id: 'notifications.kind.dunningStageSent.hint',
          message: 'When reminders went out to families. One message per run.',
        }),
      },
      {
        key: 'reconciliationReviewPending',
        label: msg({ id: 'notifications.kind.reconciliationReviewPending', message: 'Bank import needs review' }),
        hint: msg({
          id: 'notifications.kind.reconciliationReviewPending.hint',
          message: 'When an imported bank statement has payments to check. Not for statements you uploaded yourself.',
        }),
      },
      {
        key: 'timeEntriesSubmitted',
        label: msg({ id: 'notifications.kind.timeEntriesSubmitted', message: 'Time entries to approve' }),
        hint: msg({
          id: 'notifications.kind.timeEntriesSubmitted.hint',
          message: 'When an employee submits time entries for your approval.',
        }),
      },
    ],
  },
];

function PreferenceRow({
  id,
  label,
  hint,
  checked,
  disabled,
  onCheckedChange,
}: {
  id: string;
  label: string;
  hint?: string;
  checked: boolean;
  disabled: boolean;
  onCheckedChange: (checked: boolean) => void;
}) {
  return (
    <div className="flex items-center justify-between gap-4 py-2">
      <div className={cn('space-y-0.5', disabled && 'opacity-50')}>
        <Label htmlFor={id} className="text-sm font-medium">
          {label}
        </Label>
        {hint ? (
          <p id={`${id}-hint`} className="text-xs text-muted-foreground">
            {hint}
          </p>
        ) : null}
      </div>
      <Switch
        id={id}
        checked={checked}
        disabled={disabled}
        onCheckedChange={onCheckedChange}
        aria-describedby={hint ? `${id}-hint` : undefined}
      />
    </div>
  );
}

function PreferencesSkeleton() {
  // The shape of the loaded dialog: the master row, a group heading, rows with a hint line.
  return (
    <div className="space-y-4 py-2" data-testid="notification-preferences-skeleton">
      <Skeleton className="h-9 w-full" />
      <Skeleton className="h-3 w-32" />
      {[0, 1, 2].map((row) => (
        <div key={row} className="space-y-1.5">
          <Skeleton className="h-4 w-2/3" />
          <Skeleton className="h-3 w-full" />
        </div>
      ))}
    </div>
  );
}

function SaveStatus({ state }: { state: 'idle' | 'saving' | 'saved' }) {
  const { i18n } = useLingui();
  return (
    <p
      role="status"
      data-testid="notification-preferences-save-status"
      className="flex min-h-5 items-center gap-1.5 text-xs text-muted-foreground"
    >
      {state === 'saving' ? (
        <>
          <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
          {i18n._(msg({ id: 'common.saving', message: 'Saving...' }))}
        </>
      ) : state === 'saved' ? (
        <>
          <Check className="h-3.5 w-3.5 text-success-on-surface" aria-hidden="true" />
          {i18n._(msg({ id: 'notifications.dialog.saved', message: 'Saved' }))}
        </>
      ) : (
        i18n._(msg({ id: 'notifications.dialog.autosave', message: 'Each switch saves at once.' }))
      )}
    </p>
  );
}

function PreferencesContent() {
  const { i18n } = useLingui();
  const query = useNotificationPreferences();
  const update = useUpdateNotificationPreference();

  if (query.isError) {
    return (
      <DialogBody>
        <QueryErrorState
          compact
          error={query.error}
          title={i18n._(
            msg({ id: 'notifications.preferences.loadError', message: 'Could not load your notification settings.' }),
          )}
          onRetry={() => void query.refetch()}
        />
      </DialogBody>
    );
  }
  if (query.status !== 'success') {
    return (
      <DialogBody>
        <PreferencesSkeleton />
      </DialogBody>
    );
  }

  const prefs = query.data;
  const masterOn = prefs.pushEnabled;
  const set = (key: NotificationPreferenceKey) => (value: boolean) => update.mutate({ key, value });
  const saveState = update.isPending ? 'saving' : update.isSuccess ? 'saved' : 'idle';

  return (
    <>
      <DialogBody>
        <PreferenceRow
          id="notification-pref-pushEnabled"
          label={i18n._(msg({ id: 'notifications.master.label', message: 'Push notifications' }))}
          hint={i18n._(
            msg({
              id: 'notifications.dialog.master.hint',
              message: 'Turn this off to pause every kind below. Your choices are kept.',
            }),
          )}
          checked={masterOn}
          disabled={false}
          onCheckedChange={set('pushEnabled')}
        />
        <Separator className="my-2" />
        {masterOn ? null : (
          <Alert variant="info" role="note" className="my-2 px-3 py-2">
            <AlertDescription className="text-xs">
              {i18n._(
                msg({
                  id: 'notifications.dialog.masterOff',
                  message: 'Push notifications are off. Turn them on to choose which kinds you get.',
                }),
              )}
            </AlertDescription>
          </Alert>
        )}
        {MANAGEMENT_GROUPS.map((group) => (
          <section
            key={group.key}
            aria-labelledby={`notification-group-${group.key}`}
            className="pt-3 first-of-type:pt-1"
          >
            <h3
              id={`notification-group-${group.key}`}
              className="text-xs font-medium uppercase tracking-wide text-muted-foreground"
            >
              {i18n._(group.title)}
            </h3>
            {group.kinds.map((kind) => (
              <PreferenceRow
                key={kind.key}
                id={`notification-pref-${kind.key}`}
                label={i18n._(kind.label)}
                hint={i18n._(kind.hint)}
                checked={prefs[kind.key]}
                disabled={!masterOn}
                onCheckedChange={set(kind.key)}
              />
            ))}
          </section>
        ))}
      </DialogBody>
      <div className="border-t pt-3">
        <SaveStatus state={saveState} />
      </div>
    </>
  );
}

interface NotificationPreferencesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * The admin's push notification preferences (#1900), opened from the user menu.
 * The flags drive push on the Daycare Butler Management app only; the web has
 * no push. Each switch saves on its own, so the dialog has no Save button; the
 * status line under the rows says when a change has landed.
 */
export function NotificationPreferencesDialog({ open, onOpenChange }: NotificationPreferencesDialogProps) {
  const { i18n } = useLingui();
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="sm">
        <DialogHeader>
          <DialogTitle>{i18n._(NOTIFICATION_DIALOG_TITLE)}</DialogTitle>
          <DialogDescription>
            {i18n._(
              msg({
                id: 'notifications.dialog.caption',
                message: 'These settings apply to push notifications on your phone (Daycare Butler Management app).',
              }),
            )}
          </DialogDescription>
        </DialogHeader>
        {open ? <PreferencesContent /> : null}
      </DialogContent>
    </Dialog>
  );
}
